/**
 * MagicLinkAdmin.js
 * M-05 §3.1: Admin panel card for magic link management.
 * View/Copy/Rotate the company's unique NOM-035 magic link.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Link2, Copy, RefreshCw, Check, ExternalLink, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const MagicLinkAdmin = ({ empresaId }) => {
  const { language } = useLanguage();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const t = language === 'es' ? {
    title: 'Enlace de Evaluación (Magic Link)',
    desc: 'Enlace único para que los empleados completen su evaluación NOM-035 sin iniciar sesión.',
    noLink: 'No hay enlace activo. Genera uno para comenzar.',
    generate: 'Generar enlace',
    rotate: 'Rotar enlace',
    rotateWarn: 'Rotar revocará el enlace anterior. Los empleados que no hayan terminado deberán usar el nuevo enlace.',
    copy: 'Copiar',
    copied: '¡Copiado!',
    active: 'Activo',
    created: 'Creado'
  } : {
    title: 'Evaluation Link (Magic Link)',
    desc: 'Unique link for employees to complete their NOM-035 evaluation without logging in.',
    noLink: 'No active link. Generate one to get started.',
    generate: 'Generate link',
    rotate: 'Rotate link',
    rotateWarn: 'Rotating will revoke the previous link. Employees who haven\'t finished will need to use the new link.',
    copy: 'Copy',
    copied: 'Copied!',
    active: 'Active',
    created: 'Created'
  };

  useEffect(() => {
    loadLink();
  }, [empresaId]);

  const loadLink = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/magic-link`);
      if (res.data?.success && res.data.data) {
        setToken(res.data.data);
      } else {
        setToken(null);
      }
    } catch { setToken(null); }
    finally { setLoading(false); }
  };

  const generateLink = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/magic-link/generate`);
      if (res.data?.success) {
        setToken(res.data.data);
        toast.success(language === 'es' ? 'Enlace generado' : 'Link generated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setGenerating(false); }
  };

  const copyLink = () => {
    if (!token) return;
    const url = `${window.location.origin}/nom035/magic/${token.token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success(t.copied);
    setTimeout(() => setCopied(false), 2000);
  };

  const fullUrl = token ? `${window.location.origin}/nom035/magic/${token.token}` : '';

  return (
    <Card data-testid="magic-link-admin-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-5 w-5 text-teal-600" />
          {t.title}
        </CardTitle>
        <CardDescription className="text-xs">{t.desc}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> ...</div>
        ) : token ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-700">{t.active}</Badge>
              {token.created_at && (
                <span className="text-xs text-gray-400">
                  {t.created}: {new Date(token.created_at).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Input readOnly value={fullUrl} className="text-xs font-mono bg-gray-50" data-testid="magic-link-url" />
              <Button size="sm" variant="outline" onClick={copyLink} className="shrink-0" data-testid="magic-link-copy">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => window.open(fullUrl, '_blank')} className="shrink-0">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <p className="text-xs text-gray-400">{t.rotateWarn}</p>
              <Button size="sm" variant="outline" onClick={generateLink} disabled={generating}
                className="text-orange-600 border-orange-200 hover:bg-orange-50" data-testid="magic-link-rotate">
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                {t.rotate}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">{t.noLink}</p>
            <Button onClick={generateLink} disabled={generating} className="bg-teal-600 hover:bg-teal-700"
              data-testid="magic-link-generate">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
              {t.generate}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MagicLinkAdmin;
